import React from 'react'
import {
    Table,
    TableBody,
    TableFooter,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog } from '@fortawesome/free-solid-svg-icons'
import { LoadingIndicator } from 'components'
import SortComponent from 'components/DataGrid/SortComponent'
import idDirectory from './idAttributes'

import {
    ExtendedHeadersMTD,
    ExtendedColumnsMTD,
} from '../../views/MTDReports/grids/MTDReportsGrid'

interface IDataGridSlimMTD {
    data: { [key: string]: any }[]
    columns: ExtendedColumnsMTD[]
    headers: ExtendedHeadersMTD[]
    highlightColumns: string[]
    midIdentifierColumns: string[]
    isLoading: boolean
    testId: string
    className?: string
    onSettingsClick?: (rowData: any) => void
}

const RowCheckbox: any = ({ ...rest }: any) => (
    <input className="row-select-checkbox" {...rest} />
)

const EnchancedTableHead = ({
    columns,
    headers,
    testId,
    className = '',
}: {
    columns: ExtendedColumnsMTD[]
    headers: ExtendedHeadersMTD[]
    testId: string
    className?: string
}): JSX.Element => {
    return (
        <TableHead
            className={`${'emp-dataGridSlimMTD-tableHeader'} ${className}`}
            id={`${idDirectory.dataGridSlimMTD.tableHeader}-${testId}`}
        >
            <TableRow>
                {headers.map((header) => {
                    return (
                        <Tooltip
                            arrow
                            placement="top"
                            title={header.header}
                            classes={{
                                tooltip: 'emp-dataGridSlimMTD-tooltipPopper',
                                arrow: 'emp-dataGridSlimMTD-tooltipPopperArrow',
                            }}
                            key={`key-${header.header}`}
                        >
                            <TableCell
                                className={
                                    'emp-dataGridSlimMTD-headerFooterTitle'
                                }
                                colSpan={header.colSpan}
                                style={{
                                    backgroundColor: header.color,
                                    paddingLeft: '4px',
                                    top: 0,
                                }}
                                id={`${idDirectory.dataGridSlimMTD.tableHeaderCell}-${testId}-${header.header}`}
                            >
                                {header.icon ? (
                                    <img
                                        src={header.icon}
                                        className={'emp-dataGridSlimMTD-icon'}
                                        alt={header.header}
                                    />
                                ) : null}
                                {header.header.toUpperCase()}
                            </TableCell>
                        </Tooltip>
                    )
                })}
            </TableRow>
            <TableRow>
                <TableCell>
                    <RowCheckbox
                        type="checkbox"
                        style={{ transform: 'scale(1.4)' }}
                    />
                </TableCell>
                {columns.map((column) => (
                    <Tooltip
                        arrow
                        placement="top"
                        title={column.label}
                        classes={{
                            tooltip: 'emp-dataGridSlimMTD-tooltipPopper',
                            arrow: 'emp-dataGridSlimMTD-tooltipPopperArrow',
                        }}
                        key={`key-${column.accessor}`}
                    >
                        <TableCell
                            className={'emp-dataGridSlimMTD-columns'}
                            key={`key-${column.accessor}`}
                        >
                            {column.sortable ? (
                                <SortComponent
                                    sortHandler={() => null}
                                    accessor={column.accessor}
                                    value={column.label}
                                    sortInfo={{
                                        sortBy: '',
                                        sortDesc: 'desc',
                                    }}
                                />
                            ) : (
                                column.label
                            )}
                        </TableCell>
                    </Tooltip>
                ))}
            </TableRow>
        </TableHead>
    )
}
const EnchancedTableBody = ({
    data,
    columns,
    highlightColumns,
    midIdentifierColumns,
    testId,
    className = '',
    onSettingsClick,
}: {
    data: { [key: string]: any }[]
    columns: ExtendedColumnsMTD[]
    highlightColumns: string[]
    midIdentifierColumns: string[]
    testId: string
    className?: string
    onSettingsClick?: (rowData: any) => void
}): JSX.Element => {
    const tableAccessors: string[] = columns.map(
        (column: { label: string; accessor: string }) => column.accessor
    )

    const isValueGreaterThanOne = (percentageString: string) => {
        const value = parseFloat(percentageString.replace('%', ''))
        return value > 1
    }

    return (
        <TableBody
            className={`${'emp-dataGridSlimMTD-tableBody'} ${className}`}
            id={`${idDirectory.dataGridSlimMTD.tableBody}-${testId}`}
        >
            {data.map((row) => {
                const highlightCells = tableAccessors.filter(
                    (accessor: string) => {
                        return highlightColumns.includes(accessor)
                            ? isValueGreaterThanOne(row[accessor])
                            : false
                    }
                )
                return (
                    <TableRow key={`key-${row.id}`}>
                        <TableCell>
                            <RowCheckbox
                                type="checkbox"
                                style={{ transform: 'scale(1.4)' }}
                            />
                        </TableCell>

                        {tableAccessors.map((accessor: string) => {
                            if (accessor === 'settings')
                                return (
                                    <TableCell
                                        className={
                                            'emp-dataGridSlimMTD-columns'
                                        }
                                        style={{
                                            textAlign: 'center',
                                            color: '#757575',
                                            cursor: onSettingsClick ? 'pointer' : 'default',
                                        }}
                                        key={`key-${accessor}`}
                                        onClick={() => onSettingsClick && onSettingsClick(row)}
                                        id={`${idDirectory.dataGridSlimMTD.tableCell}-settings-${testId}-${row.id || 'row'}`}
                                    >
                                        <FontAwesomeIcon
                                            icon={faCog}
                                            style={{ fontSize: '14px' }}
                                        />
                                    </TableCell>
                                )

                            const highlightCell =
                                (highlightCells.length &&
                                    midIdentifierColumns.includes(accessor)) ||
                                highlightCells.includes(accessor)

                            return (
                                <TableCell
                                    className={'emp-dataGridSlimMTD-columns'}
                                    key={`key-${accessor}`}
                                    style={{
                                        ...(highlightCell && {
                                            color: 'red',
                                        }),
                                    }}
                                >
                                    {row[accessor]}
                                </TableCell>
                            )
                        })}
                    </TableRow>
                )
            })}
        </TableBody>
    )
}

const EnhancedTableFooter = ({
    headers,
    testId,
    className = '',
}: {
    headers: ExtendedHeadersMTD[]
    testId: string
    className?: string
}): JSX.Element => {
    return (
        <TableFooter
            className={`${'emp-dataGridSlimMTD-tableFooter'} ${className}`}
            id={`${idDirectory.dataGridSlimMTD.tableFooter}-${testId}`}
        >
            <TableRow style={{ verticalAlign: 'top' }}>
                <TableCell
                    colSpan={5}
                    align="right"
                    className={'emp-dataGridSlimMTD-headerFooterTitle'}
                    style={{
                        backgroundColor: '#f1f1f1',
                        padding: '4px 6px 12px 4px',
                    }}
                >
                    GRAND TOTAL
                </TableCell>
                {headers.map((header, idx) =>
                    header.total
                        ? header.total.map((item) => (
                              <TableCell
                                  className={
                                      'emp-dataGridSlimMTD-headerFooterTitle'
                                  }
                                  style={{
                                      backgroundColor: header.color,
                                      padding: '3px 0px 10px 3px',
                                      whiteSpace: 'pre',
                                  }}
                                  key={`key-${item.title}-${idx}`}
                              >
                                  {item.value}
                              </TableCell>
                          ))
                        : null
                )}
            </TableRow>
        </TableFooter>
    )
}

export const DataGridSlimMTD: React.FC<IDataGridSlimMTD> = ({
    data,
    columns,
    headers,
    highlightColumns,
    midIdentifierColumns,
    isLoading,
    testId,
    className = '',
    onSettingsClick,
}) => {
    return !isLoading ? (
        <>
            <div
                className={`emp-dataGridSlimMTD-dataGrid ${className}`}
                id={`${idDirectory.dataGridSlimMTD.divRoot}-${testId}`}
            >
                <TableContainer
                    component={Paper}
                    className={'emp-dataGridSlimMTD-tableContainer'}
                    id={`${idDirectory.dataGridSlimMTD.tableContainer}-${testId}`}
                >
                    <Table size={'small'} stickyHeader={true}>
                        <EnchancedTableHead
                            columns={columns}
                            headers={headers}
                            testId={testId}
                        />

                        <EnchancedTableBody
                            columns={columns}
                            data={data}
                            highlightColumns={highlightColumns}
                            midIdentifierColumns={midIdentifierColumns}
                            testId={testId}
                            onSettingsClick={onSettingsClick}
                        />

                        <EnhancedTableFooter
                            headers={headers}
                            testId={testId}
                        />
                    </Table>
                </TableContainer>
            </div>

            <div
                className={'emp-dataGridSlimMTD-showingResults'}
                id={`${idDirectory.dataGridSlimMTD.divEntries}-${testId}`}
            >
                {`Showing ${1} to ${10} of ${10} entries`}
            </div>
        </>
    ) : (
        <div className={'emp-dataGridSlimMTD-loadingContainer'}>
            <LoadingIndicator />
        </div>
    )
}

export default DataGridSlimMTD
